import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, LinearProgress, Stack } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useDispatch, useSelector } from 'react-redux';
import {
    useGetConteneursGeoJsonQuery,
    useGetClientChecklistActesQuery,
} from '../../../modules/conteneurMap/conteneurMap.api';
import {
    makeSelectCappedRows,
    makeSelectActiveIds,
    resetConteneurMap,
    hydrateConteneurMap,
    pickPersistedState,
    setChecklistActe,
} from '../../../modules/conteneurMap/conteneurMap.slice';
import { featuresToRows } from '../../../modules/conteneurMap/conteneurMap.helpers';
import { filterRowsByPatrimoineIds } from '../../../modules/conteneurMap/conteneurMap.slice';
import ConteneurMapToolbar from './ConteneurMapToolbar';
import ConteneurMap from './ConteneurMap';
import ConteneurList from './ConteneurList';

// Filtres persistés par client : un retour navigateur depuis la fiche d'une borne retrouve la vue telle quelle.
const storageKey = (clientId) => `patrimoineV2.conteneurMap.${clientId}`;
const readPersisted = (clientId) => {
    try {
        const raw = window.localStorage.getItem(storageKey(clientId));
        return raw ? JSON.parse(raw) : null;
    } catch (e) {
        return null;
    }
};
const writePersisted = (clientId, value) => {
    try {
        window.localStorage.setItem(storageKey(clientId), JSON.stringify(value));
    } catch (e) {
        // quota / stockage indisponible : la persistance est un confort, on ignore.
    }
};

/**
 * Vue principale split carte / liste pour Patrimoine v2 (PAT-04, floorplan List report, mobilité Mixte).
 *
 * - Un seul fetch RTK Query du GeoJSON. En mode « Maintenance » (checklist) les args incluent
 *   `mode/acte/dateDebut/dateFin` : nouvelle clé de cache, chaque feature porte `checklist`.
 * - `rows` = features.properties + lat/lng injectés.
 * - Filtrage search + patrimoines + bbox + état checklist côté client via sélecteurs memoizés.
 * - Patrimoines décochés (R7) : retirés de la carte (pas de fantômes). En mode Maintenance, un état
 *   filtré est retiré aussi ; les fantômes (gris translucide) restent le comportement du mode Lavage.
 * - Filtres / mode / panneaux persistés en localStorage par client (restaurés au montage).
 * - Mode présentation : la vue (filtres + carte) passe en `position: fixed` plein écran (TV / vidéoproj),
 *   Échap pour sortir. CSS plutôt que Fullscreen API : les popovers MUI (portails) restent visibles.
 */
function ConteneurMapView({ clientId }) {
    const dispatch = useDispatch();
    const filters = useSelector((s) => s.conteneurMap.filters);
    const mode = useSelector((s) => s.conteneurMap.mode);
    const checklist = useSelector((s) => s.conteneurMap.checklist);
    const mapBounds = useSelector((s) => s.conteneurMap.mapBounds);
    const panelMode = useSelector((s) => s.conteneurMap.panelMode);
    const showList = panelMode === 'list' || panelMode === 'both';
    const showMap = panelMode === 'map' || panelMode === 'both';
    const [presentation, setPresentation] = useState(false);
    useEffect(() => {
        if (!presentation) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') setPresentation(false); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [presentation]);
    // Reset au démontage / changement de client
    useEffect(() => () => { dispatch(resetConteneurMap()); }, [dispatch]);

    // Restauration des filtres persistés du client, puis sauvegarde à chaque changement.
    // `hydratedFor` évite d'écraser le stockage avec l'état initial avant la restauration.
    const persisted = useMemo(
        () => pickPersistedState({ mode, panelMode, checklist, filters }),
        [mode, panelMode, checklist, filters]
    );
    const [hydratedFor, setHydratedFor] = useState(null);
    useEffect(() => {
        if (!clientId) return;
        dispatch(hydrateConteneurMap(readPersisted(clientId)));
        setHydratedFor(clientId);
    }, [clientId, dispatch]);
    useEffect(() => {
        if (clientId && hydratedFor === clientId) writePersisted(clientId, persisted);
    }, [clientId, hydratedFor, persisted]);

    // Actes checklist des formulaires du client (R11) ; premier acte sélectionné par défaut.
    const { data: actes = [] } = useGetClientChecklistActesQuery(clientId, { skip: !clientId });
    useEffect(() => {
        if (mode === 'checklist' && !checklist.acte && actes.length > 0) {
            dispatch(setChecklistActe(actes[0].code));
        }
    }, [mode, checklist.acte, actes, dispatch]);
    const acteCourant = useMemo(() => actes.find((a) => a.code === checklist.acte) ?? null, [actes, checklist.acte]);

    const isChecklist = mode === 'checklist' && !!checklist.acte;
    // Le filtre de statut de lavage ne s'applique qu'en mode Lavage.
    const effectiveFilters = useMemo(
        () => (mode === 'lavage' ? filters : { ...filters, statuts: [] }),
        [mode, filters]
    );

    // Fetch global (sans patrimoineId/statut) : on garde TOUS les conteneurs en mémoire
    // pour que la carte puisse afficher les "fantômes" hors filtre, et que la toolbar
    // puisse proposer tous les patrimoines même quand un filtre est actif.
    const { data, isFetching, isError, error } = useGetConteneursGeoJsonQuery(
        isChecklist
            ? { clientId, mode: 'checklist', acte: checklist.acte, dateDebut: checklist.dateDebut, dateFin: checklist.dateFin }
            : { clientId },
        { skip: !clientId }
    );

    const features = data?.features ?? [];
    const allRows = useMemo(() => featuresToRows(features), [features]);
    // Carte : patrimoines décochés retirés (R7).
    const mapFeatures = useMemo(() => {
        if (!Array.isArray(filters.patrimoineIds)) return features;
        const set = new Set(filters.patrimoineIds.map(String));
        return features.filter((f) => f?.properties?.patrimoineId != null && set.has(String(f.properties.patrimoineId)));
    }, [features, filters.patrimoineIds]);

    const checklistFilter = isChecklist ? { etats: checklist.etats, elements: checklist.elements } : null;
    // Indicateurs : PAV des patrimoines cochés, avant filtre d'état (dénominateurs du parc sélectionné).
    const kpiRows = useMemo(
        () => (isChecklist ? filterRowsByPatrimoineIds(allRows, filters.patrimoineIds) : null),
        [isChecklist, allRows, filters.patrimoineIds]
    );

    // Sélecteur memoizé instance-bound
    const cappedSelector = useMemo(makeSelectCappedRows, []);
    // En mode liste seule, ignorer les bounds de la carte pour afficher tous les conteneurs
    const effectiveBounds = showMap ? mapBounds : null;
    const { rows: visibleRows } = cappedSelector(
        allRows, filters.search, effectiveBounds, effectiveFilters.statuts, filters.patrimoineId,
        filters.dechetTypes, filters.conteneurTypes, filters.patrimoineIds, checklistFilter
    );

    // Set<id> des conteneurs "actifs" (matchant les filtres) ; null = pas de filtre.
    const activeIdsSelector = useMemo(makeSelectActiveIds, []);
    const activeIds = activeIdsSelector(allRows, effectiveFilters, checklistFilter);

    return (
        <Box sx={presentation
            ? { display: 'flex', flexDirection: 'column', position: 'fixed', inset: 0, zIndex: 1200, bgcolor: '#fff', px: 2, pb: 2, height: '100vh' }
            : { display: 'flex', flexDirection: 'column', height: '80vh' }}
        >
            <ConteneurMapToolbar
                rows={allRows}
                actes={actes}
                acteCourant={acteCourant}
                presentation={presentation}
                onTogglePresentation={() => setPresentation((v) => !v)}
            />

            {isFetching && <LinearProgress sx={{ mb: 1 }} />}
            {isError && (
                <Alert severity="error" sx={{ mb: 1 }}>
                    Erreur lors du chargement des conteneurs
                    {error?.status ? ` (${error.status})` : ''}.
                </Alert>
            )}
            {mode === 'checklist' && actes.length === 0 && (
                <Alert severity="info" sx={{ mb: 1 }}>
                    Aucun acte de type checklist n'est rattaché aux formulaires de ce client.
                </Alert>
            )}

            <Grid container spacing={2} sx={{ flex: 1, minHeight: 0 }}>
                {showList && (
                    <Grid size={{ xs: 12, md: showMap ? 6 : 12 }} sx={{ minHeight: 0, height: '100%' }}>
                        <Stack sx={{ height: '100%' }}>
                            <ConteneurList rows={visibleRows} checklistMode={isChecklist} />
                        </Stack>
                    </Grid>
                )}
                {showMap && (
                    <Grid size={{ xs: 12, md: showList ? 6 : 12 }} sx={{ minHeight: 0, height: '100%' }}>
                        <ConteneurMap
                            features={mapFeatures}
                            activeIds={activeIds}
                            checklistMode={isChecklist}
                            heatmap={isChecklist && checklist.heatmap}
                            ghostInactive={!isChecklist}
                            kpiRows={kpiRows}
                            presentation={presentation}
                        />
                    </Grid>
                )}
            </Grid>
        </Box>
    );
}

export default ConteneurMapView;
